/**
 * CombatLogManager - Complete Combat Session Tracking System
 * 
 * This manager handles comprehensive combat logging including:
 * - Initiative order and changes
 * - All actions taken by each combatant
 * - Damage, healing, and condition applications
 * - Environmental and terrain effects
 * - Complete combat state snapshots for each turn
 * - Rewind functionality to previous combat states
 * 
 * @date_of_change 2025-08-30
 * - Initial implementation with full combat session tracking
 */

/**
 * Combat Log Entry Types
 */
export const COMBAT_LOG_TYPES = {
  COMBAT_START: 'COMBAT_START',
  COMBAT_END: 'COMBAT_END',
  ROUND_START: 'ROUND_START',
  TURN_START: 'TURN_START',
  TURN_END: 'TURN_END',
  ACTION_STANDARD: 'ACTION_STANDARD',
  ACTION_ATTACK: 'ACTION_ATTACK',
  ACTION_SPELL: 'ACTION_SPELL',
  ACTION_MOVE: 'ACTION_MOVE',
  ACTION_BONUS: 'ACTION_BONUS',
  ACTION_REACTION: 'ACTION_REACTION',
  ACTION_FREE: 'ACTION_FREE',
  DAMAGE_DEALT: 'DAMAGE_DEALT',
  HEALING_APPLIED: 'HEALING_APPLIED',
  CONDITION_APPLIED: 'CONDITION_APPLIED',
  CONDITION_REMOVED: 'CONDITION_REMOVED',
  INITIATIVE_CHANGED: 'INITIATIVE_CHANGED',
  TOKEN_MOVED: 'TOKEN_MOVED',
  TERRAIN_CHANGED: 'TERRAIN_CHANGED',
  ENVIRONMENT_EFFECT: 'ENVIRONMENT_EFFECT'
};

/**
 * Combat Session Manager
 */
class CombatLogManager {
  constructor() {
    this.combatLog = [];
    this.combatStates = [];
    this.currentRound = 1;
    this.currentTurn = 0;
    this.combatStarted = false;
    this.combatId = null;
    this.combatantMap = new Map(); // id -> { id, name, ... }
    this._lastTurnStartRef = { key: null, at: 0 };
    this._entrySequence = 0;

    // @date_of_change: 2025-10-05 - WebSocket integration for multiplayer combat log sync
    this.sessionId = null;
    this.webSocketSender = null;

    // Listen for remote combat log entries
    this.setupWebSocketListener();
  }

  /**
   * @date_of_change: 2025-10-05 - Set up WebSocket listener for remote combat log entries
   */
  setupWebSocketListener() {
    const handleRemoteLogEntry = (event) => {
      const { logEntry, isRemote } = event.detail;

      console.log('[CombatLog] WebSocket event received:', {
        logEntry: logEntry?.type,
        isRemote,
        combatStarted: this.combatStarted
      });

      // Accept remote entries even if combat isn't started yet (player might join mid-combat)
      // @date_of_change: 2025-10-05 - Removed combatStarted check to allow players joining mid-combat
      if (isRemote) {
        console.log('[CombatLog] Received remote log entry:', logEntry);
        if (logEntry?.__operation === 'update' && logEntry?.id) {
          const existingIndex = this.combatLog.findIndex((entry) => entry.id === logEntry.id);
          if (existingIndex >= 0) {
            const { __operation, ...updatedEntry } = logEntry;
            this.combatLog[existingIndex] = updatedEntry;
            window.dispatchEvent(new CustomEvent('combat-log-updated', {
              detail: { entryId: updatedEntry.id, operation: 'update', isRemote: true }
            }));
          }
          return;
        }
        // Reconnects and confirmations may deliver an entry already in the
        // local log. Identity is authoritative; never duplicate it.
        // date_of_change: 2026-08-26
        if (logEntry?.id && this.combatLog.some((entry) => entry?.id === logEntry.id)) return;
        // Add entry directly to log without re-broadcasting
        this.combatLog.push(logEntry);

        // Combat logs describe completed events. The token snapshot owns
        // economy, including whether a spell used an action or bonus action.
        // D&D Basic Rules, Your Turn. date_of_change: 2026-11-04
      }
    };

    const handleAuthoritativeHistory = (event) => {
      const logEntries = Array.isArray(event?.detail?.logEntries) ? event.detail.logEntries : [];
      const historySessionId = String(event?.detail?.sessionId || '').trim();
      if (historySessionId && this.sessionId && historySessionId !== this.sessionId) return;

      // A join payload is the complete bounded history for its routed scene.
      // Replace even when it is empty so a fresh room cannot inherit the
      // singleton log from a room visited earlier in this browser session.
      // date_of_change: 2026-08-31
      this.combatLog = logEntries.map((entry) => ({ ...entry }));
      window.dispatchEvent(new CustomEvent('combat-log-updated', {
        detail: {
          operation: 'hydrate',
          count: logEntries.length,
          isRemote: true,
          sessionId: historySessionId || this.sessionId
        }
      }));
    };

    window.addEventListener('websocket-combat-log-entry', handleRemoteLogEntry);
    window.addEventListener('websocket-combat-log-history', handleAuthoritativeHistory);
  }

  /**
   * @date_of_change: 2025-10-05 - Configure WebSocket for combat log broadcasting
   * @param {string} sessionId - The combat session ID
   * @param {function} sendFn - WebSocket send function from WebSocketContext
   */
  setWebSocketSender(sessionId, sendFn) {
    const nextSessionId = String(sessionId || '').trim() || null;
    const sessionChanged = this.sessionId !== nextSessionId;
    this.sessionId = nextSessionId;
    this.webSocketSender = sendFn;
    if (sessionChanged) {
      // CombatLogManager is a module singleton, but combat results are scoped
      // to one BattleRoom. Reset every mutable audit projection at the route
      // boundary; the new room's authoritative history hydrates afterward.
      // date_of_change: 2026-08-31
      this.combatLog = [];
      this.combatStates = [];
      this.currentRound = 1;
      this.currentTurn = 0;
      this.combatStarted = false;
      this.combatId = null;
      this.combatantMap.clear();
      this._lastTurnStartRef = { key: null, at: 0 };
      this._entrySequence = 0;
      window.dispatchEvent(new CustomEvent('combat-log-updated', {
        detail: { operation: 'session-boundary', sessionId: nextSessionId }
      }));
    }
    console.log('[CombatLog] WebSocket sender configured for session:', nextSessionId);
  }

  /**
   * Start a new combat session
   */
  startCombat(combatants, initialTokenState, { authoritativeRecovery = false } = {}) {
    this.combatId = `combat_${Date.now()}`;
    this.combatStarted = true;
    this.currentRound = 1;
    this.currentTurn = 0;
    // A joining client can receive the persisted log history before the
    // initiative projection initializes this singleton. Recovery must not
    // erase that history or publish a synthetic second COMBAT_START event.
    // A genuinely new combat still starts with a clean audit trail.
    // date_of_change: 2026-08-26
    if (!authoritativeRecovery) this.combatLog = [];
    this.combatStates = [];
    this.combatantMap.clear();
    this._lastTurnStartRef = { key: null, at: 0 };
    this._entrySequence = 0;
    combatants.forEach(c => {
      if (c?.id) this.combatantMap.set(c.id, { id: c.id, name: c.name || 'Unknown' });
    });

    // Create initial combat state snapshot
    const initialState = {
      round: 1,
      turn: 0,
      combatantId: null,
      timestamp: new Date().toISOString(),
      tokens: JSON.parse(JSON.stringify(initialTokenState)),
      combatants: JSON.parse(JSON.stringify(combatants)),
      conditions: {},
      environment: {}
    };

    this.combatStates.push(initialState);

    if (!authoritativeRecovery) {
      // Log combat start only for the client that actually starts combat.
      this.logEntry({
        type: COMBAT_LOG_TYPES.COMBAT_START,
        round: 1,
        turn: 0,
        combatantId: null,
        data: {
          combatants: combatants.map(c => ({
            id: c.id,
            name: c.name,
            initiative: c.initiative,
            hp: c.hpObj || { current: c.hp || 0, max: c.maxHp || 0 }
          }))
        },
        description: `Combat started with ${combatants.length} combatants`,
        timestamp: new Date().toISOString()
      });
    }

    console.log(`[CombatLog] Combat session ${this.combatId} started with ${combatants.length} combatants`);
    return this.combatId;
  }

  /**
   * End current combat session
   */
  endCombat(reason = 'Combat ended') {
    if (!this.combatStarted) return;

    this.logEntry({
      type: COMBAT_LOG_TYPES.COMBAT_END,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId: null,
      data: { reason },
      description: reason,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] Combat session ${this.combatId} ended: ${reason}`);
    this.combatStarted = false;
    this._lastTurnStartRef = { key: null, at: 0 };
  }

  /**
   * Reconcile a server-authoritative pre-combat snapshot without writing a
   * synthetic "combat ended" entry back to the shared log. This is used when
   * a loaded room or rewind says initiative is still pending even if an older
   * saved combat-log projection claimed combat had started.
   * date_of_change: 2026-08-25
   */
  synchronizePreCombatState({ round = 1, turn = 0 } = {}) {
    this.combatStarted = false;
    this.combatId = null;
    this.currentRound = Math.max(1, Number(round) || 1);
    this.currentTurn = Math.max(0, Number(turn) || 0);
    this._lastTurnStartRef = { key: null, at: 0 };
  }

  /**
   * Reconcile the authoritative active turn without writing a synthetic turn
   * transition into the shared log. `startCombat` initializes turn zero, so a
   * joined room must immediately restore the saved round and index.
   * date_of_change: 2026-08-26
   */
  synchronizeAuthoritativeTurn({ round = 1, turn = 0, combatants = [] } = {}) {
    this.combatStarted = true;
    this.currentRound = Math.max(1, Number(round) || 1);
    this.currentTurn = Math.max(0, Number(turn) || 0);
    (Array.isArray(combatants) ? combatants : []).forEach((combatant) => {
      if (combatant?.id) {
        this.combatantMap.set(combatant.id, {
          id: combatant.id,
          name: combatant.name || 'Unknown'
        });
      }
    });
  }

  /**
   * Start a new round
   */
  startRound(round) {
    if (!this.combatStarted) return;

    this.currentRound = round;
    this.currentTurn = 0;
    this._lastTurnStartRef = { key: null, at: 0 };

    this.logEntry({
      type: COMBAT_LOG_TYPES.ROUND_START,
      round,
      turn: 0,
      combatantId: null,
      data: { round },
      description: `Round ${round} started`,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] Round ${round} started`);
  }

  /**
   * Start a combatant's turn
   */
  startTurn(combatant, turnIndex, currentTokenState) {
    if (!this.combatStarted) return;

    const turnKey = `${this.currentRound}:${turnIndex}:${combatant?.id || 'unknown'}`;
    const now = Date.now();
    if (this._lastTurnStartRef?.key === turnKey) {
      console.warn('[CombatLog] Duplicate TURN_START suppressed:', turnKey);
      return;
    }
    // TURN_START can be emitted by both initiative controls and sync effects; logging must be
    // idempotent so the audit trail mirrors actual initiative. date_of_change: 2026-06-23
    this._lastTurnStartRef = { key: turnKey, at: now };

    this.currentTurn = turnIndex;

    // Create combat state snapshot at turn start
    const turnState = {
      round: this.currentRound,
      turn: turnIndex,
      combatantId: combatant.id,
      timestamp: new Date().toISOString(),
      tokens: JSON.parse(JSON.stringify(currentTokenState)),
      combatants: JSON.parse(JSON.stringify([combatant])),
      conditions: this.getCurrentConditions(),
      environment: this.getCurrentEnvironment()
    };

    this.combatStates.push(turnState);

    this.logEntry({
      type: COMBAT_LOG_TYPES.TURN_START,
      round: this.currentRound,
      turn: turnIndex,
      combatantId: combatant.id,
      data: {
        combatant: {
          id: combatant.id,
          name: combatant.name,
          hp: combatant.hpObj || { current: combatant.hp || 0, max: combatant.maxHp || 0 },
          conditions: combatant.conditions || []
        }
      },
      description: `${combatant.name}'s turn started`,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] ${combatant.name}'s turn started (Round ${this.currentRound}, Turn ${turnIndex})`);
  }

  /**
   * End a combatant's turn
   */
  endTurn(combatant, actionsTaken = []) {
    if (!this.combatStarted) return;

    this.logEntry({
      type: COMBAT_LOG_TYPES.TURN_END,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId: combatant.id,
      data: {
        combatant: {
          id: combatant.id,
          name: combatant.name,
          hp: combatant.hpObj || { current: combatant.hp || 0, max: combatant.maxHp || 0 }
        },
        actionsTaken: actionsTaken.length
      },
      description: `${combatant.name}'s turn ended (${actionsTaken.length} actions taken)`,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] ${combatant.name}'s turn ended with ${actionsTaken.length} actions`);
  }

  /**
   * Log an action taken by a combatant with enhanced audit trail.
   * Enhanced with dice formulas, before/after snapshots for complete audit trail.
   *
   * D&D 5e Rule Reference: Action economy tracking (PHB Ch.9)
   * date_of_change: 2025-09-19
   */
  logAction(combatantId, action, targets = [], result = {}) {
    if (!this.combatStarted) return;

    const storedAttackRoll = result.attackRoll ? {
      ...result.attackRoll,
      d20Roll: result.attackRoll.d20Roll ?? result.attackRoll.roll,
      modifier: result.attackRoll.modifier ?? result.attackRoll.attackBonus,
      isCriticalFailure: result.attackRoll.isCriticalFailure ?? result.attackRoll.isCriticalFail
    } : undefined;
    const storedDamageRoll = result.damageRoll || result.damage;

    // Capture before snapshots of all affected targets
    const before = targets.map(t => ({
      id: t.id,
      hpBefore: t.hpObj ? { ...t.hpObj } : { current: t.hp || 0, max: t.maxHp || 0 },
      resourcesBefore: t.resources ? { ...t.resources } : {},
      conditionsBefore: t.conditions ? [...t.conditions] : []
    }));

    const logEntry = {
      type: this.getActionLogType(action.type),
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId,
      data: {
        action: {
          name: action.name,
          type: action.type,
          spellLevel: action.spellLevel,
          description: action.desc || action.description || '',
          standardAction: result.standardAction || action.standardAction || action.originalAction?.standardAction,
          // Enhanced dice formula preservation
          dice: result.rolls ? result.rolls.map(r => `${r.formula} = ${r.total}`) : [],
          originalAction: action.original || action
        },
        targets: targets.map(t => ({ id: t.id, name: t.name })),
        result: {
          actionType: result.actionType,
          actionName: result.actionName,
          hit: result.hit,
          damage: result.damage,
          damageDetails: result.damageDetails,
          damageRoll: storedDamageRoll,
          attackRoll: storedAttackRoll,
          targetAC: result.targetAC ?? storedAttackRoll?.targetAC,
          targetHpBefore: result.targetHpBefore,
          targetHpRemaining: result.targetHpRemaining,
          natural20: result.natural20,
          natural1: result.natural1,
          defeated: result.defeated,
          sneakAttack: result.sneakAttack,
          hiddenAttack: result.hiddenAttack,
          totalDamage: result.totalDamage,
          healing: result.healing,
          totalHealing: result.totalHealing,
          effect: result.effect,
          standardAction: result.standardAction,
          check: result.check,
          checkRoll: result.checkRoll,
          skillCheck: result.skillCheck || result.stealthCheck || result.searchCheck,
          abilityCheck: result.abilityCheck,
          rolls: result.rolls,
          dc: result.dc,
          success: result.success,
          saveAbility: result.saveAbility,
          savingThrowInfo: result.savingThrowInfo,
          checkSuccess: result.checkSuccess,
          hiddenStealthCheck: result.hiddenStealthCheck,
          hiddenStealthTotal: result.hiddenStealthTotal,
          targetPassivePerception: result.targetPassivePerception,
          effects: result.effects || [],
          // Preserve player-authored interaction data verbatim for the GMA
          // mechanical-event packet. These fields describe a declaration,
          // never a mechanically inferred outcome.
          // date_of_change: 2026-07-18
          speech: result.speech || null,
          interactionIntent: result.interactionIntent || null,
          resolutionStatus: result.resolutionStatus || null,
          requiresGameMasterResolution: result.requiresGameMasterResolution === true,
          executionPath: result.executionPath,
          validationResults: result.validationResults
        },
        snapshots: {
          before,
          // After snapshots will be populated by the caller after mutations
          after: targets.map(t => ({
            id: t.id,
            hpAfter: t.hpObj ? { ...t.hpObj } : { current: t.hp || 0, max: t.maxHp || 0 },
            resourcesAfter: t.resources ? { ...t.resources } : {},
            conditionsAfter: t.conditions ? [...t.conditions] : []
          }))
        }
      },
      description: this.generateActionDescription(combatantId, action, targets, result),
      timestamp: new Date().toISOString()
    };

    this.logEntry(logEntry);
    console.log(`[CombatLog] Enhanced action logged: ${logEntry.description}`);
  }

  /**
   * Log damage dealt to a target
   */
  logDamage(attackerId, targetId, damageAmount, damageType = 'unknown') {
    if (!this.combatStarted) return;

    this.logEntry({
      type: COMBAT_LOG_TYPES.DAMAGE_DEALT,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId: attackerId,
      data: {
        targetId,
        damage: damageAmount,
        damageType
      },
      description: `${damageAmount} ${damageType} damage dealt to target`,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] ${damageAmount} ${damageType} damage logged`);
  }

  /**
   * Log healing applied to a target
   */
  logHealing(healerId, targetId, healingAmount) {
    if (!this.combatStarted) return;

    this.logEntry({
      type: COMBAT_LOG_TYPES.HEALING_APPLIED,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId: healerId,
      data: {
        targetId,
        healing: healingAmount
      },
      description: `${healingAmount} HP healed`,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] ${healingAmount} healing logged`);
  }

  /**
   * Log condition applied to a target
   */
  logCondition(sourceId, targetId, condition, duration = null) {
    if (!this.combatStarted) return;

    this.logEntry({
      type: COMBAT_LOG_TYPES.CONDITION_APPLIED,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId: sourceId,
      data: {
        targetId,
        condition,
        duration
      },
      description: `${condition} condition applied${duration ? ` for ${duration} rounds` : ''}`,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] Condition ${condition} applied`);
  }

  /**
   * Log token movement
   */
  logMovement(combatantId, fromPosition, toPosition, distance) {
    if (!this.combatStarted) return;

    this.logEntry({
      type: COMBAT_LOG_TYPES.TOKEN_MOVED,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId,
      data: {
        from: fromPosition,
        to: toPosition,
        distance
      },
      description: `Moved ${distance}ft from (${fromPosition.x}, ${fromPosition.y}) to (${toPosition.x}, ${toPosition.y})`,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] Movement logged: ${distance}ft`);
  }

  /**
   * Rewind combat to a specific state
   */
  rewindTo(stateIndex) {
    if (stateIndex < 0 || stateIndex >= this.combatStates.length) {
      console.error(`[CombatLog] Invalid rewind index: ${stateIndex}`);
      return null;
    }

    const targetState = this.combatStates[stateIndex];
    
    // Remove all combat states after the target
    this.combatStates = this.combatStates.slice(0, stateIndex + 1);
    
    // Remove all log entries after the target timestamp
    const targetTimestamp = targetState.timestamp;
    this.combatLog = this.combatLog.filter(entry => entry.timestamp <= targetTimestamp);
    
    // Update current combat position
    this.currentRound = targetState.round;
    this.currentTurn = targetState.turn;

    // Log the rewind action
    this.logEntry({
      type: COMBAT_LOG_TYPES.ENVIRONMENT_EFFECT,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId: null,
      data: {
        effect: 'COMBAT_REWIND',
        targetRound: targetState.round,
        targetTurn: targetState.turn
      },
      description: `Combat rewound to Round ${targetState.round}, Turn ${targetState.turn}`,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] Combat rewound to Round ${targetState.round}, Turn ${targetState.turn}`);
    return targetState;
  }

  /**
   * Reconciles the local presentation log only after the server confirms an
   * authoritative rewind. The server checkpoint remains the source of combat
   * truth; this lookup merely aligns the readable local timeline.
   * date_of_change: 2026-08-13
   */
  rewindToAuthoritativePosition(round, turn) {
    const targetRound = Number(round);
    const targetTurn = Number(turn);
    let stateIndex = -1;
    for (let index = this.combatStates.length - 1; index >= 0; index -= 1) {
      const state = this.combatStates[index];
      if (Number(state?.round) === targetRound && Number(state?.turn) === targetTurn) {
        stateIndex = index;
        break;
      }
    }
    if (stateIndex >= 0) return this.rewindTo(stateIndex);

    this.currentRound = Number.isFinite(targetRound) ? targetRound : this.currentRound;
    this.currentTurn = Number.isFinite(targetTurn) ? targetTurn : this.currentTurn;
    this.logEntry({
      type: COMBAT_LOG_TYPES.ENVIRONMENT_EFFECT,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId: null,
      data: { effect: 'AUTHORITATIVE_COMBAT_REWIND' },
      description: `Server restored combat to Round ${this.currentRound}, Turn ${this.currentTurn}`,
      timestamp: new Date().toISOString()
    });
    return null;
  }

  /**
   * Get all available rewind points
   */
  getRewindPoints() {
    return this.combatStates.map((state, index) => ({
      index,
      round: state.round,
      turn: state.turn,
      combatantId: state.combatantId,
      timestamp: state.timestamp,
      description: this.generateRewindPointDescription(state)
    }));
  }

  /**
   * Get combat log filtered by criteria
   */
  getCombatLog(filters = {}) {
    this.combatLog = this.combatLog.map((entry) => (
      entry?.id
        ? entry
        : { ...entry, id: this.createEntryId() }
    ));
    let filteredLog = [...this.combatLog];

    if (filters.round) {
      filteredLog = filteredLog.filter(entry => entry.round === filters.round);
    }

    // Action-economy replay must be scoped to the active initiative turn;
    // replaying every action by a combatant in the round falsely spends
    // movement or a bonus action on a later turn. date_of_change: 2026-07-18
    if (Number.isFinite(Number(filters.turn))) {
      filteredLog = filteredLog.filter(entry => Number(entry.turn) === Number(filters.turn));
    }

    if (filters.combatantId) {
      filteredLog = filteredLog.filter(entry => entry.combatantId === filters.combatantId);
    }

    if (filters.type) {
      filteredLog = filteredLog.filter(entry => entry.type === filters.type);
    }

    if (filters.fromTimestamp) {
      filteredLog = filteredLog.filter(entry => entry.timestamp >= filters.fromTimestamp);
    }

    return filteredLog;
  }

  /**
   * Export combat session data
   */
  exportCombatSession() {
    return {
      combatId: this.combatId,
      startTime: this.combatLog.find(e => e.type === COMBAT_LOG_TYPES.COMBAT_START)?.timestamp,
      endTime: this.combatLog.find(e => e.type === COMBAT_LOG_TYPES.COMBAT_END)?.timestamp,
      totalRounds: this.currentRound,
      totalTurns: this.combatStates.length,
      log: this.combatLog,
      states: this.combatStates
    };
  }

  /**
   * GAP-012: Build per-combatant summary statistics from the combat log.
   * Returns damage dealt, damage received, healing done, kills, crits, and spell casts.
   * date_of_change: 2026-05-20
   * @returns {Object} stats keyed by combatantId
   */
  buildCombatSummaryStats() {
    const stats = {};

    const ensureEntry = (id) => {
      if (!stats[id]) {
        stats[id] = {
          id,
          name: this.getCombatantName(id) || id,
          damageDealt: 0,
          damageReceived: 0,
          healingDone: 0,
          kills: 0,
          crits: 0,
          spellsCast: 0
        };
      }
      return stats[id];
    };

    this.combatLog.forEach((entry) => {
      const actorId = entry.combatantId;
      const d = entry.data || {};

      if (entry.type === COMBAT_LOG_TYPES.DAMAGE_DEALT) {
        if (actorId) {
          ensureEntry(actorId).damageDealt += Number(d.amount || d.damage || d.total || 0);
        }
        const targetId = d.targetId || d.target?.id;
        if (targetId) {
          ensureEntry(targetId).damageReceived += Number(d.amount || d.damage || d.total || 0);
          if (d.killed || d.ko || d.targetDown) {
            if (actorId) ensureEntry(actorId).kills += 1;
          }
        }
      }

      if (entry.type === COMBAT_LOG_TYPES.HEALING_APPLIED) {
        if (actorId) ensureEntry(actorId).healingDone += Number(d.amount || d.healing || 0);
      }

      if (entry.type === COMBAT_LOG_TYPES.ACTION_ATTACK) {
        if (actorId && d?.result?.attackRoll?.isCritical) {
          ensureEntry(actorId).crits += 1;
        }
      }

      if (entry.type === COMBAT_LOG_TYPES.ACTION_SPELL) {
        if (actorId) ensureEntry(actorId).spellsCast += 1;
      }
    });

    return stats;
  }

  /**
   * GAP-012: Serialize the combat log to a Markdown string for export.
   * Includes header, summary table, and turn-by-turn action list.
   * date_of_change: 2026-05-20
   * @returns {string} Markdown text
   */
  exportAsMarkdown() {
    const session = this.exportCombatSession();
    const stats = this.buildCombatSummaryStats();
    const lines = [];

    // --- Header ---
    lines.push('# Combat Session Log');
    lines.push('');
    if (session.combatId) lines.push(`**Session ID:** ${session.combatId}`);
    if (session.startTime) lines.push(`**Start:** ${new Date(session.startTime).toLocaleString()}`);
    if (session.endTime) lines.push(`**End:** ${new Date(session.endTime).toLocaleString()}`);
    lines.push(`**Rounds:** ${session.totalRounds}`);
    lines.push('');

    // --- Summary Statistics Table ---
    const combatantList = Object.values(stats);
    if (combatantList.length > 0) {
      lines.push('## Summary Statistics');
      lines.push('');
      lines.push('| Combatant | Dmg Dealt | Dmg Received | Healing | Kills | Crits | Spells |');
      lines.push('|-----------|-----------|--------------|---------|-------|-------|--------|');
      combatantList.forEach((s) => {
        lines.push(
          `| ${s.name} | ${s.damageDealt} | ${s.damageReceived} | ${s.healingDone} | ${s.kills} | ${s.crits} | ${s.spellsCast} |`
        );
      });
      lines.push('');
    }

    // --- Turn-by-Turn Log ---
    lines.push('## Combat Log');
    lines.push('');

    let currentRound = null;
    this.combatLog.forEach((entry) => {
      if (entry.round !== undefined && entry.round !== currentRound) {
        currentRound = entry.round;
        lines.push(`### Round ${currentRound}`);
        lines.push('');
      }
      const actor = entry.combatantId ? this.getCombatantName(entry.combatantId) : null;
      const prefix = actor ? `**${actor}**: ` : '';
      const desc = entry.description || entry.data?.message || entry.type;
      lines.push(`- ${prefix}${desc}`);
    });

    return lines.join('\n');
  }

  /**
   * Export complete combat state for session save/load
   * @date_of_change 2025-09-15 - Added for enhanced session save/load functionality
   */
  exportState() {
    return {
      combatLog: this.combatLog,
      combatStates: this.combatStates,
      currentRound: this.currentRound,
      currentTurn: this.currentTurn,
      combatStarted: this.combatStarted,
      combatId: this.combatId,
      combatantMap: this.combatantMap ? Object.fromEntries(this.combatantMap) : {}
    };
  }

  /**
   * Import complete combat state from session load
   * @date_of_change 2025-09-15 - Added for enhanced session save/load functionality
   */
  importState(state) {
    try {
      this.combatLog = state.combatLog || [];
      this.combatStates = state.combatStates || [];
      this.currentRound = state.currentRound || 1;
      this.currentTurn = state.currentTurn || 0;
      this.combatStarted = state.combatStarted || false;
      this.combatId = state.combatId || null;
      this._lastTurnStartRef = { key: null, at: 0 };

      // Restore combatant map
      this.combatantMap.clear();
      if (state.combatantMap) {
        Object.entries(state.combatantMap).forEach(([id, data]) => {
          this.combatantMap.set(id, data);
        });
      }

      console.log('[CombatLogManager] State imported successfully:', {
        logEntries: this.combatLog.length,
        stateSnapshots: this.combatStates.length,
        combatStarted: this.combatStarted,
        currentRound: this.currentRound
      });
    } catch (error) {
      console.error('[CombatLogManager] Failed to import state:', error);
      throw error;
    }
  }

  mergeState(state) {
    if (!state || typeof state !== 'object') return;

    const entryKey = (entry) => (
      entry?.id
      || [
        entry?.timestamp,
        entry?.type,
        entry?.combatantId,
        entry?.data?.action?.name,
        entry?.description
      ].map((value) => String(value ?? '')).join('|')
    );
    const mergedEntries = new Map();
    [...(Array.isArray(this.combatLog) ? this.combatLog : []), ...(Array.isArray(state.combatLog) ? state.combatLog : [])]
      .forEach((entry) => {
        if (entry) mergedEntries.set(entryKey(entry), entry);
      });
    this.combatLog = [...mergedEntries.values()].sort((left, right) => (
      new Date(left?.timestamp || 0).getTime() - new Date(right?.timestamp || 0).getTime()
    ));

    this.combatStarted = this.combatStarted || Boolean(state.combatStarted);
    this.combatId = this.combatId || state.combatId || null;
    const incomingMap = state.combatantMap instanceof Map
      ? state.combatantMap
      : new Map(Object.entries(state.combatantMap || {}));
    incomingMap.forEach((combatant, id) => {
      if (!this.combatantMap.has(id)) this.combatantMap.set(id, combatant);
    });
  }

  // Helper Methods

  createEntryId() {
    const existingIds = new Set(this.combatLog.map((entry) => entry?.id).filter(Boolean));
    let candidate;
    do {
      candidate = `${this.combatId || 'combat'}-entry-${++this._entrySequence}`;
    } while (existingIds.has(candidate));
    return candidate;
  }

  /**
   * @date_of_change: 2025-10-05 - Modified to broadcast entries via WebSocket
   */
  logEntry(entry) {
    const normalizedEntry = {
      ...entry,
      id: entry?.id || this.createEntryId()
    };
    this.combatLog.push(normalizedEntry);

    // Immediate resolution feedback and the durable log consume the same
    // committed entry. Dispatch after insertion so neither surface needs to
    // poll or recalculate the outcome. date_of_change: 2026-08-11
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('combat-log-updated', {
        detail: { entryId: normalizedEntry.id, operation: 'create' }
      }));
    }

    // Broadcast to other players via WebSocket if configured
    if (this.webSocketSender && this.sessionId) {
      this.webSocketSender('combatLogEntry', {
        sessionId: this.sessionId,
        logEntry: normalizedEntry
      });
      console.log('[CombatLog] Broadcasted log entry via WebSocket:', normalizedEntry.type);
    }
    return normalizedEntry;
  }

  /**
   * Edit only the human-readable description. Structured data and snapshots
   * remain immutable, and every edit keeps its prior text for auditability.
   * date_of_change: 2026-07-27
   */
  updateLogEntry(entryId, updates = {}, editor = {}) {
    const index = this.combatLog.findIndex((entry) => entry?.id === entryId);
    if (index < 0) return null;
    const nextDescription = String(updates.description || '').trim();
    if (!nextDescription) return null;

    const current = this.combatLog[index];
    const editedAt = new Date().toISOString();
    const updated = {
      ...current,
      description: nextDescription,
      editHistory: [
        ...(Array.isArray(current.editHistory) ? current.editHistory : []),
        {
          description: current.description || '',
          editedAt,
          editedBy: editor.name || editor.id || 'Game Master'
        }
      ],
      lastEditedAt: editedAt,
      lastEditedBy: editor.name || editor.id || 'Game Master'
    };
    this.combatLog[index] = updated;

    if (this.webSocketSender && this.sessionId) {
      this.webSocketSender('combatLogEntry', {
        sessionId: this.sessionId,
        logEntry: { ...updated, __operation: 'update' }
      });
    }
    window.dispatchEvent(new CustomEvent('combat-log-updated', {
      detail: { entryId, operation: 'update' }
    }));
    return updated;
  }

  /**
   * Get a combatant's display name by id.
   */
  getCombatantName(id) {
    if (!id) return '';
    return this.combatantMap.get(id)?.name || String(id);
  }

  /**
   * Add or update a combatant in the map (for tokens added during combat)
   * @date_of_change 2025-09-14
   */
  updateCombatant(combatant) {
    if (!combatant?.id) return;
    this.combatantMap.set(combatant.id, {
      id: combatant.id,
      name: combatant.name || 'Unknown'
    });
  }

  /**
   * Log a human-readable message to the combat log
   * Useful for displaying formatted strings alongside structured entries
   * @param {string} message - The message to log
   * @param {object} meta - Optional metadata (e.g., { combatantId })
   */
  logMessage(message, meta = {}) {
    if (!this.combatStarted) return;

    this.logEntry({
      type: COMBAT_LOG_TYPES.ENVIRONMENT_EFFECT,
      round: this.currentRound,
      turn: this.currentTurn,
      combatantId: meta.combatantId || null,
      data: { message, ...meta },
      description: message,
      timestamp: new Date().toISOString()
    });

    console.log(`[CombatLog] ${message}`);
  }

  getActionLogType(actionType) {
    const typeMap = {
      action: COMBAT_LOG_TYPES.ACTION_STANDARD,
      attack: COMBAT_LOG_TYPES.ACTION_ATTACK,
      spell: COMBAT_LOG_TYPES.ACTION_SPELL,
      move: COMBAT_LOG_TYPES.ACTION_MOVE,
      bonus: COMBAT_LOG_TYPES.ACTION_BONUS,
      reaction: COMBAT_LOG_TYPES.ACTION_REACTION,
      free: COMBAT_LOG_TYPES.ACTION_FREE
    };
    return typeMap[actionType] || COMBAT_LOG_TYPES.ACTION_STANDARD;
  }

  generateActionDescription(combatantId, action, targets, result) {
    // @date_of_change 2025-09-17 - DEBUG: Trace data flow to diagnose missing detailed display
    console.log('[CombatLogManager] generateActionDescription called with:', {
      combatantId,
      action,
      targets: targets.map(t => ({ id: t.id, name: t.name })),
      result: {
        ...result,
        resultsCount: result.results?.length,
        effectsCount: result.effects?.length,
        hasEffects: !!result.effects,
        effectTypes: result.effects?.map(e => e.type),
        fullResult: result
      }
    });

    // Narrative declarations need an exact, useful log line. A generic
    // "Speak completed" or feature "resolved" entry is materially false.
    // date_of_change: 2026-07-18
    if (result?.speech?.text) {
      const speaker = result.speech.speakerName || this.getCombatantName(combatantId) || 'A combatant';
      const audience = result.speech.targetName ? ` to ${result.speech.targetName}` : '';
      return `${speaker} says${audience}: “${result.speech.text}”`;
    }
    if (result?.interactionIntent?.text) {
      const actor = result.interactionIntent.actorName || this.getCombatantName(combatantId) || 'A combatant';
      const target = result.interactionIntent.targetName ? ` targeting ${result.interactionIntent.targetName}` : '';
      const feature = result.interactionIntent.featureName || result.actionName || action?.name || 'interaction';
      return `${actor} declares ${feature}${target}: ${result.interactionIntent.text} (awaiting GMA resolution)`;
    }

    // @date_of_change 2025-09-18 - ROBUST ACTION NAME: Fallbacks to avoid 'undefined' in log
    const targetNames = targets.map(t => t.name).join(', ');
    const safeActionName = (action && (action.name || action.originalAction?.name)) || (result && result.actionName) || 'Action';
    let desc = `${safeActionName}`;

    if (targets.length > 0) {
      desc += ` against ${targetNames}`;
    }

    if (result.hit !== undefined) {
      desc += result.hit ? ' (HIT)' : ' (MISS)';
    }

    if (result.damage) {
      // @date_of_change 2025-09-14 - Fix "[object Object]" display by properly handling damage object
      const damageAmount = typeof result.damage === 'object' ? result.damage.total : result.damage;
      desc += ` - ${damageAmount} damage`;
    }

    if (result.attackRoll?.rollType === 'advantage') {
      desc += ' [advantage]';
    } else if (result.attackRoll?.rollType === 'disadvantage') {
      desc += ' [disadvantage]';
    }

    if (result.sneakAttack) {
      desc += `\n• Sneak Attack: +${result.sneakAttack.total} damage (${result.sneakAttack.dice})`;
    }

    if (result.healing) {
      desc += ` - ${result.healing} healing`;
    }

    if (result.effect && result.hit === undefined && !result.damage && !result.healing) {
      desc += ` - ${result.effect}`;
    } else if (!result.effect && action?.type === 'action') {
      const genericDescription = action?.desc || action?.description || action?.originalAction?.description;
      if (genericDescription) {
        desc += ` - ${genericDescription}`;
      }
    }

    // @date_of_change 2025-10-25 - EXTRA DICE DISPLAY (Bless/Bane)
    if (result.attackRoll && Array.isArray(result.attackRoll.extraDice) && result.attackRoll.extraDice.length > 0) {
      const parts = result.attackRoll.extraDice.map(ed => {
        const sign = ed.total < 0 ? '' : '+'; // negatives already include minus in total
        const shown = Math.abs(ed.total);
        const rollsText = Array.isArray(ed.rolls) && ed.rolls.length > 0 ? ` [${ed.rolls.join(', ')}]` : '';
        return `${ed.source}: ${sign}${shown} (${ed.formula})${rollsText}`;
      });
      if (parts.length > 0) {
        desc += `\n• Roll modifiers: ${parts.join('; ')}`;
      }
    }

    // @date_of_change 2025-09-17 - Add detailed saving throw and damage effects display
    if (result.effects && Array.isArray(result.effects) && result.effects.length > 0) {
      const savingThrowEffects = result.effects.filter(e => e.type === 'savingThrow');

      if (savingThrowEffects.length > 0) {
        desc += '\n\nSaving Throw Results:';

        savingThrowEffects.forEach(effect => {
          const save = effect.savingThrow;
          const damage = effect.damage;

          if (save) {
            const saveResult = save.success ? 'SUCCESS' : 'FAILURE';
            const advantageText = save.advantage ? ' (ADV)' : save.disadvantage ? ' (DIS)' : '';
            const abilityLabel = (typeof save.ability === 'string' && save.ability)
              ? save.ability.toUpperCase()
              : 'SAVE';
            const rollVal = (save.roll !== undefined && save.roll !== null) ? save.roll : '?';
            const modVal = (save.modifier !== undefined && save.modifier !== null) ? save.modifier : 0;
            const totalVal = (save.total !== undefined && save.total !== null) ? save.total : '?';
            const dcVal = (save.dc !== undefined && save.dc !== null) ? save.dc : '?';
            desc += `\n• ${effect.target}: ${abilityLabel} save (${rollVal}+${modVal}${advantageText}) = ${totalVal} vs DC ${dcVal} [${saveResult}]`;

            if (Array.isArray(save.extraDice) && save.extraDice.length > 0) {
              const parts = save.extraDice.map(ed => {
                const sign = ed.total < 0 ? '' : '+';
                const shown = Math.abs(ed.total);
                const rollsText = Array.isArray(ed.rolls) && ed.rolls.length > 0 ? ` [${ed.rolls.join(', ')}]` : '';
                return `${ed.source}: ${sign}${shown} (${ed.formula})${rollsText}`;
              });
              if (parts.length > 0) {
                desc += `\n   · Modifiers: ${parts.join('; ')}`;
              }
            }
          }

          if (damage) {
            const resistanceText = damage.resistanceApplied || '';
            const saveText = damage.halfDamage ? ' (half on save)' : '';

            if (damage.total === 0 && resistanceText.includes('IMMUNE')) {
              desc += `\n  → Immune to ${damage.type} damage (takes 0 damage)`;
            } else if (resistanceText.includes('RESISTANT')) {
              desc += `\n  → Takes ${damage.total} ${damage.type} damage (resistant - halved from ${damage.originalTotal})`;
            } else if (resistanceText.includes('VULNERABLE')) {
              desc += `\n  → Takes ${damage.total} ${damage.type} damage (vulnerable - doubled from ${Math.floor(damage.originalTotal / 2)})`;
            } else {
              desc += `\n  → Takes ${damage.total} ${damage.type} damage${saveText}${resistanceText}`;
            }
          }
        });

        if (result.totalDamage) {
          desc += `\n\nTotal Damage Dealt: ${result.totalDamage}`;
        }
      }

      // @date_of_change 2025-09-19 - HEALING EFFECTS DISPLAY: Add detailed healing effects display
      const healingEffects = result.effects.filter(e => e.type === 'healing');
      if (healingEffects.length > 0) {
        desc += '\n\nHealing Results:';

        healingEffects.forEach(effect => {
          const healing = effect.healing;
          if (healing) {
            const formulaText = healing.formula ? ` (${healing.formula})` : '';
            const rollsText = healing.rolls ? ` [${healing.rolls.join(', ')}]` : '';
            desc += `\n• ${effect.target}: Heals ${healing.total} hit points${formulaText}${rollsText}`;
          }
        });

        if (result.totalHealing) {
          desc += `\n\nTotal Healing Applied: ${result.totalHealing}`;
        }
      }
    }

    return desc;
  }

  generateRewindPointDescription(state) {
    if (state.round === 1 && state.turn === 0) {
      return 'Combat Start';
    }
    
    if (state.combatantId) {
      const combatant = state.combatants.find(c => c.id === state.combatantId);
      return `Round ${state.round} - ${combatant?.name || 'Unknown'}'s Turn`;
    }
    
    return `Round ${state.round}, Turn ${state.turn}`;
  }

  getCurrentConditions() {
    // Extract current conditions from combat state
    // This would be expanded based on how conditions are tracked
    return {};
  }

  getCurrentEnvironment() {
    // Extract current environment effects from combat state
    // This would be expanded based on environmental tracking needs
    return {};
  }
}

// Create a singleton instance
const combatLogManager = new CombatLogManager();

export default combatLogManager;
